import { useEffect, useMemo, useState } from "react";
import {
  DESKTOP_SIZE_BREAKPOINT_MIN,
  IScreenOrientation,
  IScreenSizes,
  LARGE_MOBILE_SIZE_BREAKPOINT_MAX,
  MOBILE_SIZE_BREAKPOINT_MAX,
  SMALL_MOBILE_SIZE_BREAKPOINT_MAX,
  TABLET_SIZE_BREAKPOINT_MAX,
  TABLET_SIZE_BREAKPOINT_MIN,
} from "../theme/sizes";
import { useWindowSizes } from "./useWindowSizes";

type MediaQuery = {
  minWidth?: number;
  maxWidth?: number;
};

const useMediaQuery = ({ minWidth, maxWidth }: MediaQuery): boolean => {
  const { width } = useWindowSizes();

  return useMemo(() => {
    if (typeof minWidth !== "undefined" && typeof maxWidth === "undefined") {
      return width >= minWidth;
    }
    if (typeof minWidth === "undefined" && typeof maxWidth !== "undefined") {
      return width <= maxWidth;
    }

    if (typeof maxWidth !== "undefined" && typeof minWidth !== "undefined") {
      return width >= minWidth && width <= maxWidth;
    }

    return true;
  }, [width, maxWidth, minWidth]);
};

const useScreenSizes = (): IScreenSizes => {
  const isDesktopScreen = useMediaQuery({
    minWidth: DESKTOP_SIZE_BREAKPOINT_MIN,
  });
  const isTabletScreen = useMediaQuery({
    minWidth: TABLET_SIZE_BREAKPOINT_MIN,
    maxWidth: TABLET_SIZE_BREAKPOINT_MAX,
  });
  const isMobileScreen = useMediaQuery({
    maxWidth: MOBILE_SIZE_BREAKPOINT_MAX,
  });

  const [sizes, setSizes] = useState<IScreenSizes>({
    desktop: isDesktopScreen,
    tablet: isTabletScreen,
    mobile: isMobileScreen,
  });

  useEffect(() => {
    setSizes({
      desktop: isDesktopScreen,
      tablet: isTabletScreen,
      mobile: isMobileScreen,
    });
  }, [isDesktopScreen, isTabletScreen, isMobileScreen]);

  return sizes;
};

const useDesktopSize = (): boolean => {
  return useMediaQuery({
    minWidth: DESKTOP_SIZE_BREAKPOINT_MIN,
  });
};

const useTabletSize = (): boolean => {
  return useMediaQuery({
    minWidth: TABLET_SIZE_BREAKPOINT_MIN,
    maxWidth: TABLET_SIZE_BREAKPOINT_MAX,
  });
};

const useMobileSize = (): boolean => {
  return useMediaQuery({
    maxWidth: MOBILE_SIZE_BREAKPOINT_MAX,
  });
};

const useSmallMobileSize = (): boolean => {
  return useMediaQuery({
    maxWidth: SMALL_MOBILE_SIZE_BREAKPOINT_MAX,
  });
};

const useLargeMobileSize = (): boolean => {
  return useMediaQuery({
    minWidth: LARGE_MOBILE_SIZE_BREAKPOINT_MAX,
  });
};

const useScreenOrientation = (): IScreenOrientation => {
  const { width, height } = useWindowSizes();

  return useMemo(() => {
    return {
      portrait: height >= width,
      landscape: width >= height,
    };
  }, [height, width]);
};

export type { MediaQuery };
export {
  useScreenOrientation,
  useLargeMobileSize,
  useSmallMobileSize,
  useMobileSize,
  useDesktopSize,
  useTabletSize,
  useScreenSizes,
  useMediaQuery,
};
